import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  TrendingUp, ShoppingBag, Users, Package, ArrowRight, ArrowUp, ArrowDown,
  Calendar, Clock, CheckCircle, AlertCircle, Star, Zap, Target, Award,
  BarChart3, PieChart, Activity, DollarSign, Eye, Calculator, FileText,
  Sparkles, Flame, Trophy, Rocket, MessageCircle, Book, RefreshCw
} from 'lucide-react';
import { WeatherWidget } from '@/components/ui/WeatherWidget';
import { useDashboard } from '@/hooks/useDashboard';

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const [dateRange, setDateRange] = useState<'week' | 'month' | 'quarter' | 'year'>('month');
  const { data, loading, error, refresh } = useDashboard(dateRange);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pl-PL', {
      style: 'currency',
      currency: 'PLN',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'urgent': return 'text-red-500 bg-red-900/20 border-red-500/30';
      case 'warning': return 'text-amber-500 bg-amber-900/20 border-amber-500/30';
      case 'ok': return 'text-emerald-500 bg-emerald-900/20 border-emerald-500/30';
      default: return 'text-gray-500 bg-gray-900/20 border-gray-500/30';
    }
  };

  const getTrendIcon = (value: number) => {
    if (value > 5) return <ArrowUp className="w-4 h-4 text-emerald-500" />;
    if (value < -5) return <ArrowDown className="w-4 h-4 text-red-500" />;
    return <Activity className="w-4 h-4 text-amber-500" />;
  };

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'offer_created': return <FileText className="w-5 h-5" />;
      case 'offer_accepted': return <CheckCircle className="w-5 h-5" />;
      case 'offer_rejected': return <AlertCircle className="w-5 h-5" />;
      case 'client_added': return <Users className="w-5 h-5" />;
      case 'offer_viewed': return <Eye className="w-5 h-5" />;
      default: return <Activity className="w-5 h-5" />;
    }
  };

  const formatTimeAgo = (timestamp: string) => {
    const now = new Date();
    const date = new Date(timestamp);
    const diff = now.getTime() - date.getTime();
    
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    
    if (minutes < 60) return `${minutes} minut temu`;
    if (hours < 24) return `${hours} godzin${hours === 1 ? 'ę' : 'y'} temu`;
    if (days === 1) return 'wczoraj';
    if (days < 7) return `${days} dni temu`;
    
    return date.toLocaleDateString('pl-PL');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-zinc-900 via-zinc-900 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-12 h-12 text-purple-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-400">Ładowanie danych...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-zinc-900 via-zinc-900 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <p className="text-red-400 mb-4">Błąd ładowania danych: {error}</p>
          <button
            onClick={refresh}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
          >
            Spróbuj ponownie
          </button>
        </div>
      </div>
    );
  }

  if (!data) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-zinc-900 via-zinc-900 to-slate-900">
      {/* Hero Section with Animated Background */}
      <div className="relative overflow-hidden">
        {/* Animated gradient background */}
        <div className="absolute inset-0 bg-gradient-to-br from-purple-900/20 via-pink-900/20 to-indigo-900/20">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-pink-500/10 rounded-full blur-3xl animate-pulse animation-delay-2000" />
          <div className="absolute top-1/2 left-1/2 w-96 h-96 bg-indigo-500/10 rounded-full blur-3xl animate-pulse animation-delay-4000" />
        </div>

        {/* Content */}
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-8 md:py-12">
          <div className="text-center mb-8 md:mb-12">
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 md:mb-6 bg-gradient-to-r from-white via-purple-200 to-pink-200 bg-clip-text text-transparent animate-gradient-x">
              Witaj w PlexiSystem! 🚀
            </h1>
            <p className="text-base sm:text-lg md:text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed px-4">
              Twój centrum dowodzenia. Sprawdź statystyki, zarządzaj ofertami i rozwijaj biznes.
            </p>
          </div>

          {/* Date Range Selector */}
          <div className="flex justify-center mb-8">
            <div className="bg-zinc-800/50 backdrop-blur-xl rounded-xl p-1 border border-zinc-700/50">
              {(['week', 'month', 'quarter', 'year'] as const).map((range) => (
                <button
                  key={range}
                  onClick={() => setDateRange(range)}
                  className={`px-4 py-2 rounded-lg transition-all ${
                    dateRange === range
                      ? 'bg-purple-600 text-white'
                      : 'text-gray-400 hover:text-white'
                  }`}
                >
                  {range === 'week' ? 'Tydzień' :
                   range === 'month' ? 'Miesiąc' :
                   range === 'quarter' ? 'Kwartał' : 'Rok'}
                </button>
              ))}
            </div>
          </div>

          {/* Quick Actions */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8 md:mb-12">
            <button
              onClick={() => navigate('/offer/new')}
              className="group bg-gradient-to-br from-purple-600 to-pink-600 p-6 rounded-2xl hover:shadow-2xl hover:shadow-purple-500/25 transition-all duration-500 hover:scale-105"
            >
              <div className="flex items-center justify-between">
                <div className="text-left">
                  <p className="text-white/80 text-sm mb-1">Szybka akcja</p>
                  <p className="text-white font-bold text-lg">Nowa oferta</p>
                </div>
                <div className="p-3 bg-white/20 rounded-xl group-hover:scale-110 transition-transform">
                  <FileText className="w-6 h-6 text-white" />
                </div>
              </div>
              <ArrowRight className="w-5 h-5 text-white/60 mt-4 group-hover:translate-x-2 transition-transform" />
            </button>

            <button
              onClick={() => navigate('/calculator')}
              className="group bg-gradient-to-br from-emerald-600 to-teal-600 p-6 rounded-2xl hover:shadow-2xl hover:shadow-emerald-500/25 transition-all duration-500 hover:scale-105"
            >
              <div className="flex items-center justify-between">
                <div className="text-left">
                  <p className="text-white/80 text-sm mb-1">Wycena</p>
                  <p className="text-white font-bold text-lg">Kalkulator</p>
                </div>
                <div className="p-3 bg-white/20 rounded-xl group-hover:scale-110 transition-transform">
                  <Calculator className="w-6 h-6 text-white" />
                </div>
              </div>
              <ArrowRight className="w-5 h-5 text-white/60 mt-4 group-hover:translate-x-2 transition-transform" />
            </button>

            <button
              onClick={() => navigate('/marketplace')}
              className="group bg-gradient-to-br from-amber-600 to-orange-600 p-6 rounded-2xl hover:shadow-2xl hover:shadow-amber-500/25 transition-all duration-500 hover:scale-105"
            >
              <div className="flex items-center justify-between">
                <div className="text-left">
                  <p className="text-white/80 text-sm mb-1">Katalog</p>
                  <p className="text-white font-bold text-lg">Marketplace</p>
                </div>
                <div className="p-3 bg-white/20 rounded-xl group-hover:scale-110 transition-transform">
                  <ShoppingBag className="w-6 h-6 text-white" />
                </div>
              </div>
              <ArrowRight className="w-5 h-5 text-white/60 mt-4 group-hover:translate-x-2 transition-transform" />
            </button>

            <button
              onClick={() => navigate('/clients')}
              className="group bg-gradient-to-br from-blue-600 to-indigo-600 p-6 rounded-2xl hover:shadow-2xl hover:shadow-blue-500/25 transition-all duration-500 hover:scale-105"
            >
              <div className="flex items-center justify-between">
                <div className="text-left">
                  <p className="text-white/80 text-sm mb-1">CRM</p>
                  <p className="text-white font-bold text-lg">Klienci</p>
                </div>
                <div className="p-3 bg-white/20 rounded-xl group-hover:scale-110 transition-transform">
                  <Users className="w-6 h-6 text-white" />
                </div>
              </div>
              <ArrowRight className="w-5 h-5 text-white/60 mt-4 group-hover:translate-x-2 transition-transform" />
            </button>
          </div>
        </div>
      </div>

      {/* Main Stats Cards */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 -mt-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-8 md:mb-12">
          <div className="bg-zinc-800/50 backdrop-blur-xl rounded-2xl p-6 border border-zinc-700/50 hover:border-purple-500/50 transition-all duration-500 hover:shadow-xl hover:shadow-purple-500/10">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl shadow-lg">
                <TrendingUp className="w-6 h-6 text-white" />
              </div>
              <span className={`text-sm font-bold flex items-center gap-1 ${data.stats.offersChange > 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                {data.stats.offersChange > 0 ? <ArrowUp className="w-4 h-4" /> : <ArrowDown className="w-4 h-4" />}
                {Math.abs(data.stats.offersChange).toFixed(1)}%
              </span>
            </div>
            <p className="text-gray-400 text-sm mb-1">Liczba ofert</p>
            <p className="text-3xl font-bold text-white">{data.stats.totalOffers}</p>
            <div className="mt-4 h-2 bg-zinc-700 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-purple-500 to-pink-500 rounded-full" style={{ width: `${Math.min(100, data.stats.totalOffers / 10)}%` }} />
            </div>
          </div>

          <div className="bg-zinc-800/50 backdrop-blur-xl rounded-2xl p-6 border border-zinc-700/50 hover:border-emerald-500/50 transition-all duration-500 hover:shadow-xl hover:shadow-emerald-500/10">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-xl shadow-lg">
                <DollarSign className="w-6 h-6 text-white" />
              </div>
              <span className={`text-sm font-bold flex items-center gap-1 ${data.stats.revenueChange > 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                {data.stats.revenueChange > 0 ? <ArrowUp className="w-4 h-4" /> : <ArrowDown className="w-4 h-4" />}
                {Math.abs(data.stats.revenueChange).toFixed(1)}%
              </span>
            </div>
            <p className="text-gray-400 text-sm mb-1">Przychód</p>
            <p className="text-3xl font-bold text-white">{formatCurrency(data.stats.totalRevenue)}</p>
            <div className="mt-4 h-2 bg-zinc-700 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full animate-pulse" style={{ width: '85%' }} />
            </div>
          </div>

          <div className="bg-zinc-800/50 backdrop-blur-xl rounded-2xl p-6 border border-zinc-700/50 hover:border-amber-500/50 transition-all duration-500 hover:shadow-xl hover:shadow-amber-500/10">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-gradient-to-br from-amber-500 to-orange-500 rounded-xl shadow-lg">
                <Users className="w-6 h-6 text-white" />
              </div>
              <span className={`text-sm font-bold flex items-center gap-1 ${data.stats.clientsChange > 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                {data.stats.clientsChange > 0 ? <ArrowUp className="w-4 h-4" /> : <ArrowDown className="w-4 h-4" />}
                {Math.abs(data.stats.clientsChange).toFixed(1)}%
              </span>
            </div>
            <p className="text-gray-400 text-sm mb-1">Aktywni klienci</p>
            <p className="text-3xl font-bold text-white">{data.stats.totalClients}</p>
            <div className="mt-4 h-2 bg-zinc-700 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-amber-500 to-orange-500 rounded-full" style={{ width: `${Math.min(100, data.stats.totalClients / 10)}%` }} />
            </div>
          </div>

          <div className="bg-zinc-800/50 backdrop-blur-xl rounded-2xl p-6 border border-zinc-700/50 hover:border-blue-500/50 transition-all duration-500 hover:shadow-xl hover:shadow-blue-500/10">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-xl shadow-lg">
                <Package className="w-6 h-6 text-white" />
              </div>
              <Sparkles className="w-5 h-5 text-yellow-400 animate-pulse" />
            </div>
            <p className="text-gray-400 text-sm mb-1">Produkty w ofercie</p>
            <p className="text-3xl font-bold text-white">{data.stats.totalProducts}</p>
            <div className="mt-4 h-2 bg-zinc-700 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full" style={{ width: '95%' }} />
            </div>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
          {/* Left Column - Recent Activities & Top Products */}
          <div className="lg:col-span-2 space-y-6">
            {/* Recent Activities */}
            {data.recentActivities.length > 0 && (
              <div className="bg-zinc-800/50 backdrop-blur-xl rounded-2xl p-6 border border-zinc-700/50">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-bold text-white flex items-center gap-3">
                    <Activity className="w-6 h-6 text-purple-400" />
                    Ostatnia aktywność
                  </h2>
                  <button
                    onClick={() => navigate('/offers')}
                    className="text-sm text-purple-400 hover:text-purple-300 transition-colors"
                  >
                    Zobacz wszystkie →
                  </button>
                </div>
                <div className="space-y-4">
                  {data.recentActivities.map((activity) => (
                    <div key={activity.id} className="flex items-start gap-4 p-4 bg-zinc-900/50 rounded-xl hover:bg-zinc-900/70 transition-all">
                      <div className={`p-2 rounded-lg ${activity.color}`}>
                        {getActivityIcon(activity.type)}
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-white">{activity.description}</p>
                      </div>
                      <span className="text-xs text-gray-500">{formatTimeAgo(activity.timestamp)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Top Products */}
            {data.productData.length > 0 && (
              <div className="bg-zinc-800/50 backdrop-blur-xl rounded-2xl p-6 border border-zinc-700/50">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-bold text-white flex items-center gap-3">
                    <Trophy className="w-6 h-6 text-amber-400" />
                    Najpopularniejsze produkty
                  </h2>
                  <button
                    onClick={() => navigate('/products')}
                    className="text-sm text-amber-400 hover:text-amber-300 transition-colors"
                  >
                    Zobacz katalog →
                  </button>
                </div>
                <div className="space-y-3">
                  {(data.productData || []).slice(0, 5).map((product, index) => (
                    <div key={product.name} className="flex items-center gap-4 p-4 bg-zinc-900/50 rounded-xl hover:bg-zinc-900/70 transition-all">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-lg ${
                        index === 0 ? 'bg-gradient-to-br from-amber-400 to-amber-500 text-black' :
                        index === 1 ? 'bg-gradient-to-br from-gray-300 to-gray-400 text-black' :
                        index === 2 ? 'bg-gradient-to-br from-orange-600 to-orange-700 text-white' :
                        'bg-zinc-700 text-gray-300'
                      }`}>
                        {index + 1}
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-white">{product.name}</p>
                        <div className="flex items-center gap-4 mt-1">
                          <span className="text-sm text-gray-400">{product.count} szt.</span>
                          <span className="text-sm text-emerald-400 font-medium">{formatCurrency(product.value)}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {getTrendIcon(product.percentage)}
                        {index === 0 && <Flame className="w-5 h-5 text-orange-500 animate-pulse" />}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Sales Performance */}
            {data.salesPerformance.length > 0 && (
              <div className="bg-zinc-800/50 backdrop-blur-xl rounded-2xl p-6 border border-zinc-700/50">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-bold text-white flex items-center gap-3">
                    <BarChart3 className="w-6 h-6 text-blue-400" />
                    Wydajność sprzedaży
                  </h2>
                </div>
                <div className="space-y-3">
                  {data.salesPerformance.map((person) => (
                    <div key={person.salesperson} className="p-4 bg-zinc-900/50 rounded-xl">
                      <div className="flex items-center justify-between mb-2">
                        <p className="font-medium text-white">{person.salesperson}</p>
                        <span className="text-sm text-emerald-400 font-medium">{formatCurrency(person.revenue)}</span>
                      </div>
                      <div className="grid grid-cols-3 gap-4 text-sm">
                        <div>
                          <p className="text-gray-500">Oferty</p>
                          <p className="text-white font-medium">{person.offersCount}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Konwersja</p>
                          <p className="text-white font-medium">{person.conversionRate.toFixed(1)}%</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Śr. wartość</p>
                          <p className="text-white font-medium">{formatCurrency(person.avgDealSize)}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right Column - Top Clients & Stats */}
          <div className="space-y-6">
            {/* Status Overview */}
            <div className="bg-zinc-800/50 backdrop-blur-xl rounded-2xl p-6 border border-zinc-700/50">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-white flex items-center gap-3">
                  <PieChart className="w-6 h-6 text-green-400" />
                  Status ofert
                </h2>
              </div>
              <div className="space-y-3">
                {data.statusData.map((status) => (
                  <div key={status.name} className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: status.color }} />
                      <span className="text-sm text-gray-300">{status.name}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-white">{status.value}</span>
                      <span className="text-xs text-gray-500">({status.percentage.toFixed(1)}%)</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Top Clients */}
            {data.topClients.length > 0 && (
              <div className="bg-zinc-800/50 backdrop-blur-xl rounded-2xl p-6 border border-zinc-700/50">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-bold text-white flex items-center gap-3">
                    <Star className="w-6 h-6 text-yellow-400" />
                    Top klienci
                  </h2>
                  <button
                    onClick={() => navigate('/clients')}
                    className="text-sm text-yellow-400 hover:text-yellow-300 transition-colors"
                  >
                    Zobacz wszystkich →
                  </button>
                </div>
                <div className="space-y-3">
                  {(data.topClients || []).slice(0, 5).map((client, index) => (
                    <div key={client.id} className="p-4 bg-zinc-900/50 rounded-xl hover:bg-zinc-900/70 transition-all">
                      <div className="flex items-center justify-between mb-2">
                        <p className="font-medium text-white text-sm truncate flex-1 mr-2">{client.name}</p>
                        {index === 0 && <Trophy className="w-5 h-5 text-yellow-400" />}
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-400">{client.ordersCount} ofert</span>
                        <span className="text-emerald-400 font-medium">{formatCurrency(client.revenue)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Achievement Card */}
            <div className="bg-gradient-to-br from-purple-900/50 to-pink-900/50 backdrop-blur-xl rounded-2xl p-6 border border-purple-500/20">
              <div className="flex items-center justify-between mb-4">
                <Award className="w-8 h-8 text-yellow-400" />
                <Rocket className="w-6 h-6 text-purple-400 animate-bounce" />
              </div>
              <h3 className="text-lg font-bold text-white mb-2">Świetna robota! 🎉</h3>
              <p className="text-sm text-gray-300 mb-4">
                Konwersja ofert wynosi {data.stats.conversionRate.toFixed(1)}%
                {data.stats.conversionChange > 0 && ` (+${data.stats.conversionChange.toFixed(1)}%)`}
              </p>
              <div className="flex items-center gap-2">
                <div className="flex-1 h-3 bg-purple-900/50 rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-purple-400 to-pink-400 rounded-full" style={{ width: `${data.stats.conversionRate}%` }} />
                </div>
                <span className="text-xs text-purple-300 font-bold">{data.stats.conversionRate.toFixed(1)}%</span>
              </div>
            </div>

            {/* Quick Tips */}
            <div className="bg-zinc-800/50 backdrop-blur-xl rounded-2xl p-6 border border-zinc-700/50">
              <div className="flex items-center gap-3 mb-4">
                <Zap className="w-6 h-6 text-yellow-400" />
                <h3 className="text-lg font-bold text-white">Szybka porada</h3>
              </div>
              <p className="text-sm text-gray-300 leading-relaxed">
                💡 Wykorzystaj <span className="text-purple-400 font-medium">Marketplace</span> do szybkiego tworzenia ofert. 
                Gotowe szablony produktów oszczędzą Ci nawet 70% czasu!
              </p>
              <button
                onClick={() => navigate('/marketplace')}
                className="mt-4 text-sm text-purple-400 hover:text-purple-300 transition-colors flex items-center gap-2"
              >
                Sprawdź Marketplace
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>

            {/* Weather Widget */}
            <WeatherWidget />
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="mt-8 md:mt-12 bg-gradient-to-r from-purple-900/30 via-pink-900/30 to-indigo-900/30 backdrop-blur-xl rounded-2xl md:rounded-3xl p-6 md:p-8 border border-purple-500/20 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-purple-500/10 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-pink-500/10 rounded-full blur-3xl" />
          
          <div className="relative flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div>
              <h2 className="text-xl md:text-2xl font-bold text-white mb-2">
                Potrzebujesz pomocy z systemem? 🤝
              </h2>
              <p className="text-gray-300 max-w-2xl text-sm md:text-base">
                Nasz zespół wsparcia jest dostępny 24/7. Skontaktuj się z nami lub sprawdź bazę wiedzy.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 md:gap-4 w-full md:w-auto">
              <button
                onClick={() => navigate('/knowledge')}
                className="px-4 sm:px-6 py-3 bg-white/10 backdrop-blur text-white rounded-xl hover:bg-white/20 transition-all flex items-center justify-center gap-2 w-full sm:w-auto"
              >
                <Book className="w-5 h-5" />
                Baza wiedzy
              </button>
              <button className="px-4 sm:px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl hover:shadow-xl hover:shadow-purple-500/25 transition-all flex items-center justify-center gap-2 w-full sm:w-auto">
                <MessageCircle className="w-5 h-5" />
                Czat wsparcia
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;